import React from 'react';
import {StyleProp, StyleSheet, View, ViewStyle} from 'react-native';

import config, {Brand} from '../../config';
import theme from '../../style/theme';

import RewardsProgramImage from './ProgramImage';
import {rewardsBranding} from './rewardsBranding';

// DIS-1824: SipnSave and Thirsty Camel paint the create-account banner colour
// (rewardsBranding.liteBannerSurface, the source of these literals; its SHORTCUT is
// declared there) with the on-light mark, so the Lite screens match the comp.
// Bottlemart and Harry Brown keep the theme band.
const usesRewardsSurface =
  config.BASE_TENANT === Brand.SIPNSAVE || config.BASE_TENANT === Brand.THIRSTY_CAMEL;

type RewardsBannerProps = {
  /** Applied after the brand styles, so a consumer override wins. */
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

/**
 * Rewards banner (DIS-1730): shared by the Lite sign-in entry screen and the
 * Lite sign-up form. Every brand renders a flat band carrying the rewards
 * wordmark. SipnSave and Thirsty Camel paint `rewardsBranding.liteBannerSurface` with
 * the on-light mark; Bottlemart and Harry Brown paint the brand-primary band
 * with the on-dark mark.
 *
 * The Bottlemart and Harry Brown band uses `background.button.primary`, not
 * `background.general.primary`: every brand theme pairs `button.primary` with `contrastOnPrimary: LMG_WHITE`,
 * whereas `general.primary` is LMG_YELLOW on SipnSave.
 *
 * The banner owns no corner radius (the design shows a square panel). A
 * consumer that needs rounded corners clips it in its own container.
 */
export default function RewardsBanner({style, testID = 'banner-header'}: RewardsBannerProps) {
  // `style` is appended only when given, so a banner rendered without one keeps
  // the exact style array OptIn has always produced.
  const brandStyle = [
    styles.header,
    usesRewardsSurface && {backgroundColor: rewardsBranding.liteBannerSurface},
  ];

  return (
    <View style={style ? [...brandStyle, style] : brandStyle} testID={testID}>
      <RewardsProgramImage
        testID="program-image"
        style={styles.headerImage}
        variant={usesRewardsSurface ? 'onLight' : 'onDark'}
      />
    </View>
  );
}

// SHORTCUT: the 20 vertical padding is a literal carried over from the OptIn
// header. Ceiling: no L0 spacing token layer exists. Upgrade path: a
// design-system spacing module under app/design-system/tokens/ - DIS-1737
// blocks it, DIS-1735 is the epic. The colours below are NOT shortcuts; they
// are theme-resolved.
const styles = StyleSheet.create({
  header: {
    backgroundColor: theme.colour.background.button.primary,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  headerImage: {
    marginVertical: 0,
    alignSelf: 'center',
  },
});
